import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { TOpsTrainerClass } from '../../../requests/opsTrainer';
import {
  addMyUnavailability,
  apiMessage,
  apiStatus,
  getMyTeaching,
  getMyUnavailability,
  removeMyUnavailability,
} from '../../../requests/opsInstructors';
import {
  btnGhost,
  btnPrimary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../../Admin/ui';

const NOT_LINKED =
  'Your login is not linked to an instructor yet — ask the scheduling team.';

/** Wed, Mar 26 · 9:00 AM – 12:00 PM PDT — the zone is the viewer's. */
export function fmtWhen(start: string | null, end: string | null): string {
  if (!start) return '—';
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const day = s.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const t = (d: Date, zone?: boolean) =>
    d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: zone ? 'short' : undefined,
    });
  return e ? `${day} · ${t(s)} – ${t(e, true)}` : `${day} · ${t(s, true)}`;
}

const ClassRows: React.FC<{
  items: TOpsTrainerClass[];
  empty: string;
  hint: string;
}> = ({ items, empty, hint }) => {
  const navigate = useNavigate();
  if (items.length === 0) {
    return <EmptyState title={empty} hint={hint} />;
  }
  return (
    <div className="overflow-x-auto">
      <table className={tableClass}>
        <thead className={theadClass}>
          <tr>
            <th className={thClass}>When</th>
            <th className={thClass}>Class</th>
            <th className={thClass}>Account</th>
            <th className={thClass}>Type</th>
            <th className={`${thClass} text-right`}>Registrations</th>
            <th className={thClass}>Room</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr
              key={c.id}
              className={rowClickClass}
              onClick={() => navigate(PATH.teachingClass.replace(':id', c.id))}
            >
              <td className={`${tdClass} whitespace-nowrap text-gray-three`}>
                {fmtWhen(c.start, c.end)}
              </td>
              <td className={`${tdClass} font-medium text-gray-four`}>
                {c.name ?? '(unnamed class)'}
                {c.series?.count != null && (
                  <span className="ml-2 rounded bg-grey-100 px-1.5 py-0.5 text-[11px] font-medium text-grey-700">
                    {c.series.index}/{c.series.count}
                  </span>
                )}
              </td>
              <td className={tdClass}>{c.accountName ?? '—'}</td>
              <td className={tdClass}>
                <Pill value={c.classType} tone="info" />
              </td>
              <td className={`${tdClass} text-right`}>{c.registered}</td>
              <td className={`${tdClass} whitespace-nowrap`}>
                {c.zoomRoom ? (
                  <>
                    <span>{c.zoomRoom.name ?? 'Zoom'}</span>
                    {c.zoomRoom.joinUrl && (
                      <a
                        className="ml-2 font-medium text-blue-primary hover:underline"
                        href={c.zoomRoom.joinUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Join ↗
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-gray-two">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/**
 * Away dates — the instructor tells the scheduler when they are not
 * available (PTO, a personal day). A class already booked inside a block
 * is listed under it so both sides see the clash the moment it is entered.
 */
const AwayDates: React.FC = () => {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['teaching', 'unavailability'],
    queryFn: getMyUnavailability,
    retry: false,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['teaching', 'unavailability'] });
  const add = useMutation({
    mutationFn: addMyUnavailability,
    onSuccess: (b) => {
      showToast(
        b.conflicts.length
          ? `Saved — ${b.conflicts.length} class${
              b.conflicts.length === 1 ? ' is' : 'es are'
            } booked inside these dates; the scheduler can see it`
          : 'Saved',
        b.conflicts.length ? 'warning' : 'success',
      );
      setFrom('');
      setTo('');
      setNote('');
      refresh();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });
  const remove = useMutation({
    mutationFn: removeMyUnavailability,
    onSuccess: refresh,
    onError: (err) => showToast(apiMessage(err), 'error'),
  });
  const items = data?.items ?? [];
  return (
    <Card
      title="Away dates"
      right={
        <span className="text-xs text-gray-bold">
          Days you are not available to teach
        </span>
      }
    >
      <form
        className="grid gap-3 border-b border-white-three px-5 py-4 md:grid-cols-[auto_auto_1fr_auto] md:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (!from) return;
          add.mutate({
            from,
            to: to || undefined,
            note: note.trim() || undefined,
          });
        }}
      >
        <div>
          <label className={labelClass}>From</label>
          <input
            type="date"
            required
            className={inputClass}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>To (same day if blank)</label>
          <input
            type="date"
            className={inputClass}
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Note (optional)</label>
          <input
            className={`${inputClass} w-full`}
            maxLength={200}
            placeholder="PTO, conference, no Fridays…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className={btnPrimary}
          disabled={!from || add.isPending}
        >
          {add.isPending ? 'Saving…' : 'Add'}
        </button>
      </form>
      {isLoading ? (
        <div className="px-5 py-4 text-sm text-gray-bold">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-5 py-4 text-sm text-gray-bold">
          No away dates yet. Add the days you cannot teach and the schedule will
          flag anything booked over them.
        </div>
      ) : (
        <ul className="divide-y divide-white-three">
          {items.map((b) => (
            <li
              key={b.id}
              className="flex flex-wrap items-start justify-between gap-3 px-5 py-3 text-sm"
            >
              <div>
                <div className="font-medium text-gray-four">
                  {fmtDay(b.from)}
                  {b.to.slice(0, 10) !== b.from.slice(0, 10)
                    ? ` – ${fmtDay(b.to)}`
                    : ''}
                  {b.note && (
                    <span className="ml-2 font-normal text-gray-bold">
                      {b.note}
                    </span>
                  )}
                </div>
                {b.conflicts.length > 0 && (
                  <div className="mt-1 text-xs text-yellow-900">
                    Booked inside these dates:{' '}
                    {b.conflicts
                      .map(
                        (c) =>
                          `${c.name ?? 'class'} (${fmtDay(c.start ?? b.from)})`,
                      )
                      .join(', ')}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={btnGhost}
                disabled={remove.isPending}
                onClick={() => remove.mutate(b.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

/** The instructor's schedule: what is coming up, and what they taught lately. */
const MyClasses: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['teaching'],
    queryFn: getMyTeaching,
    retry: false,
  });

  return (
    <>
      <PageHeader
        title="Your classes"
        meta={
          data
            ? `${data.upcoming.length} coming up · ${data.past.length} recent`
            : undefined
        }
      />
      <PageBody>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <Card>
            <EmptyState
              title={
                apiStatus(error) === 403
                  ? 'Nothing to show yet'
                  : 'Could not load your classes'
              }
              hint={apiStatus(error) === 403 ? NOT_LINKED : apiMessage(error)}
            />
          </Card>
        ) : (
          data && (
            <div className="grid gap-5">
              <Card title="Coming up">
                <ClassRows
                  items={data.upcoming}
                  empty="Nothing scheduled yet"
                  hint="Classes assigned to you appear here as soon as they are scheduled."
                />
              </Card>
              <Card title="Recent">
                <ClassRows
                  items={data.past}
                  empty="No classes taught yet"
                  hint="Once a class has run it moves down here, roster and all."
                />
              </Card>
              <AwayDates />
            </div>
          )
        )}
      </PageBody>
    </>
  );
};

export default MyClasses;
